// Conversion between the German date format shown to the user (d.m.Y) and the ISO format
// (YYYY-MM-DD) used on the wire and by Cally's calendar components.
//
// Both parsers validate by round-trip: JS `Date` silently rolls 31.02.2026 over into March,
// so an out-of-range day must be rejected explicitly rather than trusted.

const ISO_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})$/
// Accepts "1.2.2026", "01.02.2026" and "1.2.26". Slash is tolerated because some keyboards
// make it the easier key; hyphen deliberately is not, so it cannot collide with ISO input.
const GERMAN_PATTERN = /^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})$/

// Two-digit years below the pivot are read as 20xx, at or above it as 19xx. Matches the
// server-side convention in app/services/csv_imports/date_parser.rb.
const TWO_DIGIT_YEAR_PIVOT = 68

function pad(number, length = 2) {
  return String(number).padStart(length, "0")
}

function expandYear(year) {
  if (year.length === 4) return Number(year)

  const short = Number(year)
  return short < TWO_DIGIT_YEAR_PIVOT ? 2000 + short : 1900 + short
}

// Returns "YYYY-MM-DD" only when the calendar actually contains that day.
function buildIso(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null

  const date = new Date(Date.UTC(year, month - 1, day))
  const roundTrips =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day

  return roundTrips ? `${pad(year, 4)}-${pad(month)}-${pad(day)}` : null
}

// "01.02.2026" -> "2026-02-01". Also passes ISO straight through so pasted values work.
// Returns null for anything unparseable or for a day that does not exist.
export function parseGermanDate(value) {
  if (typeof value !== "string") return null

  const trimmed = value.trim()
  if (trimmed === "") return null

  const german = trimmed.match(GERMAN_PATTERN)
  if (german) {
    const [, day, month, year] = german
    return buildIso(expandYear(year), Number(month), Number(day))
  }

  const iso = trimmed.match(ISO_PATTERN)
  if (iso) {
    const [, year, month, day] = iso
    return buildIso(Number(year), Number(month), Number(day))
  }

  return null
}

// "2026-02-01" -> "01.02.2026". Returns "" for blank or unparseable input so it can be
// assigned straight to an input value.
export function formatGermanDate(value) {
  if (typeof value !== "string") return ""

  const match = value.trim().match(ISO_PATTERN)
  if (!match) return ""

  const [, year, month, day] = match
  if (!buildIso(Number(year), Number(month), Number(day))) return ""

  return `${pad(day)}.${pad(month)}.${pad(year, 4)}`
}

// A JS Date (as handed to Cally's getDayParts) -> "YYYY-MM-DD". Cally builds its day dates in
// UTC, so the UTC accessors are the correct ones here.
export function isoFromUtcDate(date) {
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
}

// The *local* calendar day as "YYYY-MM-DD". Deliberately not isoFromUtcDate(new Date()): that
// reads UTC accessors off a local timestamp, so in UTC+1/+2 every hour before 01:00/02:00
// resolves to the previous day and "Heute" would select yesterday.
// The `now` parameter exists so the boundary is assertable.
export function todayIso(now = new Date()) {
  return `${pad(now.getFullYear(), 4)}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}
